import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, Search, Filter, Plus, Loader, Edit, 
  Trash2, School, AlertTriangle, Star 
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Admin Professors Page component
 * Manages professors across all schools in the system
 */
const AdminProfessorsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [professors, setProfessors] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [sortOrder, setSortOrder] = useState('name'); // 'name', 'rating', 'popularity'
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  
  useEffect(() => {
    // Redirect if user is not admin
    if (user && user.role !== 'admin') {
      toast.error('You do not have permission to access this page');
      navigate('/school');
      return;
    }
    
    // Fetch data if user is admin
    if (user && user.role === 'admin') {
      fetchData();
    }
  }, [user, navigate, sortOrder]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all schools first
      const schoolsRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!schoolsRes.ok) {
        throw new Error('Failed to fetch schools');
      }
      
      const schoolsData = await schoolsRes.json();
      setSchools(schoolsData);
      
      // Fetch all professors from each school
      const professorsPromises = schoolsData.map(school => 
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${school.id}/professors?sort=${sortOrder}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }).then(res => {
          if (!res.ok) {
            console.error(`Failed to fetch professors for school ${school.id}`);
            return [];
          }
          return res.json();
        })
      );
      
      const professorsResults = await Promise.all(professorsPromises);
      // Flatten and combine all professors with their school info
      const allProfessors = professorsResults.flatMap((schoolProfs, i) => 
        schoolProfs.map(prof => ({
          ...prof,
          schoolId: schoolsData[i].id,
          schoolName: schoolsData[i].name
        }))
      );
      
      setProfessors(allProfessors);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteProfessor = async (professorId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/professors/${professorId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete professor');
      }
      
      // Update professors list
      setProfessors(professors.filter(prof => prof.id !== professorId));
      
      // Close confirmation dialog
      setDeleteConfirmation(null);
      
      toast.success('Professor deleted successfully');
    } catch (error) {
      console.error('Error deleting professor:', error);
      toast.error(error.message || 'Failed to delete professor');
    }
  };
  
  // Filter professors based on search query and school
  const filteredProfessors = professors.filter(professor => {
    const matchesSearch = 
      professor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (professor.email && professor.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (professor.bio && professor.bio.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesSchool = selectedSchool ? professor.schoolId === selectedSchool : true;
    
    return matchesSearch && matchesSchool;
  });
  
  // Render star rating
  const renderStarRating = (rating) => {
    if (!rating) return 'No ratings';
    
    return (
      <div className="flex items-center">
        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 mr-1" />
        <span>{rating.toFixed(1)}</span>
      </div>
    );
  };
  
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Professor Management
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage all professors across schools
          </p>
        </div>
        
        <Link
          to="/professors/new"
          className="mt-4 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <Plus className="mr-2 -ml-1 h-5 w-5" />
          Add Professor
        </Link>
      </div>
      
      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search professors by name, email, or bio..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-red-500 focus:border-red-500"
          />
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
        >
          <Filter className="h-4 w-4 mr-1" />
          <span className="text-sm">{showFilters ? 'Hide filters' : 'Show filters'}</span>
        </button>
        
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="school-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  School
                </label>
                <select
                  id="school-filter"
                  value={selectedSchool}
                  onChange={(e) => setSelectedSchool(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">All Schools</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sort By
                </label>
                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="name">Name</option>
                  <option value="rating">Rating</option>
                  <option value="popularity">Popularity</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Professors Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
          <span className="text-gray-600 dark:text-gray-400">Loading professors...</span>
        </div>
      ) : filteredProfessors.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <User className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No professors found
          </h3>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            {searchQuery || selectedSchool ? 
              'No professors match your current filters.' : 
              'No professors available yet. Get started by adding a professor.'}
          </p>
          {(searchQuery || selectedSchool) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedSchool('');
              }}
              className="mt-4 text-red-600 dark:text-red-400 hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    School
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Rating
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProfessors.map((professor) => (
                  <tr key={professor.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <School className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                        {professor.schoolName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <Link to={`/professors/${professor.id}`} className="hover:underline">
                        {professor.name || 'Unnamed Professor'}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {professor.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {renderStarRating(professor.avgRating)}
                      <span className="text-xs ml-2">({professor.ratingCount || 0} reviews)</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/professors/${professor.id}/edit`}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                      >
                        <Edit className="h-4 w-4 inline mr-1" />
                        Edit
                      </Link>
                      <button
                        onClick={() => setDeleteConfirmation(professor)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4 inline mr-1" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center text-red-600 dark:text-red-400 mb-4">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-medium">Confirm Deletion</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the professor <strong>{deleteConfirmation.name}</strong>? This action cannot be undone and will remove all associated ratings.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteProfessor(deleteConfirmation.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfessorsPage;